// Shared types, formatting and variance helpers for the Research tab's
// weekday analysis — used by the ticket-breakdown modal, the XLSX exporters
// and the PDF exporters so all three agree on the numbers.

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Session label used when a sale carries no train service at all (walk-up / untimed / refunds). */
export const NO_SERVICE = '(not service-attributed)'

/**
 * Indicative seats per service — matches the Capacity dashboard's assumptions.
 * Standard rake 7 x 64 = 448; Autocar / railcar 48.
 */
export const svcCapacity = (label: string): number =>
  (/autocar|railcar|limited seating/i.test(label) ? 48 : 448)

// ---------------------------------------------------------------------------
//  Payload shapes
// ---------------------------------------------------------------------------

/** One basis (this year / last year) of the per-slice detail endpoint. */
export interface DetailBasis {
  /** Running days in the slice — the divisor for EVERY per-day figure below. */
  days: number
  items: { item: string; qty: number; net: number }[]
  routes: { item: string; route: string; qty: number; net: number }[]
  services: { direction: string; dep_time: string; service: string; qty: number; net: number }[]
}

export interface DetailPayload {
  season: string
  day_no: number
  y: number | null
  m: number | null
  wom: number | null
  from: string; to: string; prev_from: string; prev_to: string
  cur: DetailBasis
  prev: DetailBasis
}

/** Grain key on the bulk export rows. `wom` is 0 for season scope, `mo` pins the month. */
export interface ExpKey { wom: number; yr: number; mo: number; day_no: number }

export interface ExportBasis {
  days: (ExpKey & { days: number })[]
  items: (ExpKey & { item: string; qty: number; net: number })[]
  routes: (ExpKey & { item: string; route: string; qty: number; net: number })[]
  services: (ExpKey & { direction: string; dep_time: string; service: string; qty: number; net: number })[]
}

export interface ExportPayload {
  season: string
  scope: 'month' | 'season'
  m: number | null
  from: string; to: string; prev_from: string; prev_to: string
  cur: ExportBasis
  prev: ExportBasis
}

// ---------------------------------------------------------------------------
//  Formatting
// ---------------------------------------------------------------------------

const gbpFmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
const numFmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

export const gbp0 = (v: number): string => gbpFmt.format(Math.round(v))
export const num0 = (v: number): string => numFmt.format(Math.round(v))
export const gbpK = (v: number): string => (Math.abs(v) >= 1000 ? `£${(v / 1000).toFixed(1)}k` : `£${Math.round(v)}`)

/** Signed currency, for variance cells. */
export const gbpSigned = (v: number): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${gbp0(Math.abs(v))}`

export const pct1 = (v: number): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}%`

/** Plain share percentage (can be negative when a bucket is net-refund). */
export const share1 = (v: number): string => `${v.toFixed(1)}%`

// ---------------------------------------------------------------------------
//  Variance
// ---------------------------------------------------------------------------

export interface Variance {
  /** Absolute change in £ per running day (this year minus last year). */
  delta: number
  /** Percentage change against last year, or null when last year is zero/absent. */
  pct: number | null
  /** true = better this year, false = worse, null = no basis for comparison. */
  better: boolean | null
}

/**
 * Compare two per-running-day figures.
 *
 * A null pct means "no meaningful percentage" — last year was zero (or the slot
 * had no running day at all), so any change is an infinite increase. The caller
 * renders that as "new" rather than a misleading number. `delta` is still valid
 * in that case and is what drives the colour.
 */
export function variance(curPerDay: number, prevPerDay: number, prevDays: number): Variance {
  const delta = curPerDay - prevPerDay
  const comparable = prevDays > 0 && Math.abs(prevPerDay) > 0.005
  const pct = comparable ? (delta / Math.abs(prevPerDay)) * 100 : null
  const better = prevDays === 0 ? null : delta > 0 ? true : delta < 0 ? false : null
  return { delta, pct, better }
}

/** Safe per-running-day division — returns 0 when the slice had no running days. */
export const perDay = (net: number, days: number): number => (days > 0 ? net / days : 0)

export const VAR_GREEN = '#2e7d32'
export const VAR_RED = '#b23b2e'

/** Hex colour for a variance cell, or undefined when there's nothing to signal. */
export const varianceColor = (v: Variance): string | undefined =>
  (v.better === true ? VAR_GREEN : v.better === false ? VAR_RED : undefined)

// ---------------------------------------------------------------------------
//  Row merging (this year + last year into one row set)
// ---------------------------------------------------------------------------

export interface MergedRow {
  key: string
  label: string
  curQty: number; curNet: number
  prevQty: number; prevNet: number
}

/**
 * Merge a this-year and a last-year list into one row set keyed by `keyOf`.
 * Rows present in only one year still appear, with zeroes on the other side —
 * dropping them would hide both new lines and discontinued ones.
 */
export function mergeRows<T>(
  cur: T[], prev: T[],
  keyOf: (r: T) => string,
  qtyOf: (r: T) => number,
  netOf: (r: T) => number,
): MergedRow[] {
  const out = new Map<string, MergedRow>()
  const take = (list: T[], side: 'cur' | 'prev') => {
    for (const r of list) {
      const key = keyOf(r)
      const row = out.get(key) ?? { key, label: key, curQty: 0, curNet: 0, prevQty: 0, prevNet: 0 }
      if (side === 'cur') { row.curQty += qtyOf(r); row.curNet += netOf(r) }
      else { row.prevQty += qtyOf(r); row.prevNet += netOf(r) }
      out.set(key, row)
    }
  }
  take(cur, 'cur')
  take(prev, 'prev')
  // Biggest this year first; lines that only existed last year fall to the end
  // ordered by what they used to be worth.
  return [...out.values()].sort((a, b) => (b.curNet - a.curNet) || (b.prevNet - a.prevNet))
}

/** Sum a merged row set into a single total row. */
export function sumRows(rows: MergedRow[]): Omit<MergedRow, 'key' | 'label'> {
  return rows.reduce(
    (t, r) => ({ curQty: t.curQty + r.curQty, curNet: t.curNet + r.curNet, prevQty: t.prevQty + r.prevQty, prevNet: t.prevNet + r.prevNet }),
    { curQty: 0, curNet: 0, prevQty: 0, prevNet: 0 })
}

// ---------------------------------------------------------------------------
//  Slice labelling
// ---------------------------------------------------------------------------

/** Prior season label, e.g. '2026/2027' -> '2025/2026'. */
export const priorSeason = (season: string): string =>
  season.split('/').map((n) => String(Number(n) - 1)).join('/')

/** Filename-safe slug. */
export const slug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

/** Trigger a real file download. Never window.open + print. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke on the next tick so Safari has actually started the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
